import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FilterEditor, FilterRecord, Operator } from '@services/core-services/filters';
import { ReactiveFormsModule } from '@angular/forms';
import { InputsComponent } from '@enginuity/core/molecules/inputs/inputs.component';
import { CalendarComponent } from '@enginuity/core/molecules/calendar/calendar.component';
import { DateRange } from '@services/core-services/models';
import { CalendarRangeComponent } from '@enginuity/core/molecules/calendar-range/calendar-range.component';
import { DateTime } from 'luxon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'app-filter-entry-editor-date',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    CalendarComponent,
    CalendarRangeComponent,
    InputsComponent,
  ],
  providers: [DatePipe],
  templateUrl: './filter-entry-editor-date.component.html',
  styleUrl: './filter-entry-editor-date.component.scss',
})
export class FilterEntryEditorDateComponent implements FilterEditor {
  private _record!: FilterRecord;
  protected readonly Operator = Operator;
  protected selectedRange: DateRange | null = null;
  calendarOpen: boolean = false;
  @ViewChild('singleCalendar') singleCalendar?: CalendarComponent;
  @ViewChild('rangeCalendar') rangeCalendar?: CalendarRangeComponent;

  get record(): FilterRecord {
    return this._record;
  }

  @Input()
  set record(record: FilterRecord) {
    this._record = record;
    this.initialize(record.rule.value);
  }

  openCalendar(): void {
    this.calendarOpen = !this.calendarOpen;
  }

  get isRange(): boolean {
    return Operator.Between === this.record.rule.operator;
  }

  @Output() onChange: EventEmitter<string | string[] | number | undefined> = new EventEmitter();

  handleCalendarChange(date: Date | null): void {
    if (!date) return;

    const localDateTime = DateTime.fromJSDate(date).startOf('day');
    const utcDateTime = localDateTime.toUTC();

    this.selectedRange = { start: date, end: date };
    const isoString = utcDateTime.toISO();
    if (isoString) {
      this.emitChange(isoString);
    }
    this.calendarOpen = false;
  }

  handleCustomRangeChange(range: DateRange | null): void {
    if (!range?.start || !range?.end) return;

    const localStart = DateTime.fromJSDate(range.start).startOf('day');
    const localEnd = DateTime.fromJSDate(range.end).endOf('day');

    const utcStart = localStart.toUTC();
    const utcEnd = localEnd.toUTC();

    this.selectedRange = range;
    const startISO = utcStart.toISO();
    const endISO = utcEnd.toISO();
    if (startISO && endISO) {
      this.emitChange([startISO, endISO]);
    }
    this.calendarOpen = false;
  }

  private emitChange(value: string | string[] | undefined): void {
    if (this.record.rule.operator !== Operator.LastX) {
      this._record.rule.value = value;
    }
    this.onChange.emit(value);
  }

  private initialize(value: string | [string, string] | number | undefined): void {
    this.selectedRange = null;

    if (this.record.rule.operator === Operator.LastX) {
      if (typeof value === 'number' && value > 0) {
        this._record.rule.value = value;
      } else if (typeof value === 'string') {
        const num = parseInt(value, 10);
        if (!isNaN(num) && num > 0) {
          this._record.rule.value = num;
        } else {
          this._record.rule.value = undefined;
        }
      }
      // Case 3: Value is an array of ISO date strings (from API/transformed query)
      else if (Array.isArray(value) && value.length === 2) {
        try {
          const start = DateTime.fromISO(value[0]);
          const end = DateTime.fromISO(value[1]);

          if (start.isValid && end.isValid) {
            const diffInDays = Math.round(end.diff(start, 'days').days);
            if (diffInDays > 0) {
              this._record.rule.value = diffInDays;
            } else {
              this._record.rule.value = undefined;
            }
          } else {
            this._record.rule.value = undefined;
          }
        } catch (error) {
          this._record.rule.value = undefined;
        }
      } else {
        this._record.rule.value = undefined;
      }
    } else {
      this._record.rule.value = value;
      if (!value) return;

      try {
        if (Array.isArray(value) && value.length === 2) {
          const start = DateTime.fromISO(value[0]).startOf('day');
          const end = DateTime.fromISO(value[1]).endOf('day');
          if (start.isValid && end.isValid) {
            this.selectedRange = {
              start: start.toJSDate(),
              end: end.toJSDate(),
            };
          }
        } else if (typeof value === 'string') {
          const date = DateTime.fromISO(value).startOf('day');
          if (date.isValid) {
            const jsDate = date.toJSDate();
            this.selectedRange = { start: jsDate, end: jsDate };
          }
        }
      } catch (error) {
        this.selectedRange = null;
        this._record.rule.value = undefined;
      }
    }
  }

  get lastXDays(): number | null {
    if (
      this.record?.rule?.operator === Operator.LastX &&
      typeof this.record.rule.value === 'number'
    ) {
      const days = this.record.rule.value;
      return days > 0 ? days : null;
    }
    return null;
  }

  onNumericChange(value: string | number | null | undefined): void {
    if (this.record.rule.operator !== Operator.LastX) {
      return;
    }

    if (value === null || value === undefined || value === '') {
      this._record.rule.value = undefined;
      this.onChange.emit(undefined);
      return;
    }

    const days = parseInt(String(value), 10);

    if (!isNaN(days) && days > 0) {
      this._record.rule.value = days;

      this.onChange.emit(days);
    } else {
      this._record.rule.value = undefined;
      this.onChange.emit(undefined);
    }
  }

  get formattedDate(): string {
    if (this.record.rule.operator === Operator.LastX) {
      return '';
    }

    if (!this.selectedRange?.start) return 'Select date(s)';

    const format = 'dd MMM yy';

    if (
      this.isRange &&
      this.selectedRange.end &&
      this.selectedRange.start.getTime() !== this.selectedRange.end.getTime()
    ) {
      const startStr = DateTime.fromJSDate(this.selectedRange.start).toFormat(format);
      const endStr = DateTime.fromJSDate(this.selectedRange.end).toFormat(format);
      return `${startStr} - ${endStr}`;
    }

    if (this.selectedRange.start) {
      return DateTime.fromJSDate(this.selectedRange.start).toFormat(format);
    }

    return 'Select date(s)';
  }
}
